import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ClipboardCheck } from "lucide-react";
import { usePOS } from "@/context/POSContext";
import { PageShell } from "@/components/PageShell";
import { Badge } from "@/components/ui/badge";
import { Segmented } from "@/components/ui/segmented";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState, ErrorState, LoadingState } from "@/components/ui/StateBoundaries";
import * as approvalsApi from "@/lib/approvalsApi";
import ApprovalDetail from "@/components/approvals/ApprovalDetail";
import { STATUS_TONE, TYPE_LABELS, ageText, typeLabel } from "@/components/approvals/shared";

const STATUSES = [
  { value: "pending", label: "Waiting" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "all", label: "All" },
];
const ALL_TYPES = "all";

/**
 * Approvals: everything the AI assistant staged over MCP waits here for a person
 * (owner brief 2026-11-04; bible §67). Approving posts the change as the approver.
 */
export default function ApprovalsPage() {
  const { token } = usePOS();
  const navigate = useNavigate();
  const { id } = useParams();
  const [status, setStatus] = useState("pending");
  const [type, setType] = useState(ALL_TYPES);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const data = await approvalsApi.listActions({ status, actionType: type === ALL_TYPES ? "" : type }, token);
      setRows(data.items || []);
    } catch (e) {
      setError(approvalsApi.errorText(e));
    }
  }, [status, type, token]);

  useEffect(() => { if (!id) load(); }, [id, load]);

  const decided = () => window.dispatchEvent(new Event("approvals-changed"));

  if (id) {
    return (
      <PageShell testId="approvals-page">
        <ApprovalDetail id={id} token={token} onBack={() => navigate("/approvals")} onDecided={decided} />
      </PageShell>
    );
  }

  return (
    <PageShell
      caption="Changes the AI assistant has prepared. Nothing happens until you approve it."
      testId="approvals-page"
    >
      <div className="flex flex-wrap items-center gap-2 shrink-0" data-testid="approvals-filters">
        <Segmented value={status} onValueChange={setStatus} options={STATUSES} aria-label="Status" />
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="h-9 w-48 max-w-full" aria-label="Type" data-testid="approvals-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_TYPES}>All types</SelectItem>
            {Object.entries(TYPE_LABELS).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {error ? <ErrorState title="Could not load the requests" message={error} onRetry={load} />
        : rows === null ? <LoadingState message="Loading requests…" />
        : rows.length === 0 ? <EmptyState icon={ClipboardCheck} title={status === "pending" ? "Nothing waiting for you" : "No requests here"} message="New requests from the assistant show up here, and as a number beside Approvals in the menu." />
        : (
          <div className="rounded-md border overflow-auto" data-testid="approvals-list">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead><TableHead>Type</TableHead><TableHead>What it does</TableHead>
                  <TableHead className="hidden md:table-cell">Requested by</TableHead><TableHead>Age</TableHead><TableHead className="hidden md:table-cell w-20">Flags</TableHead><TableHead className="hidden md:table-cell">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(row => (
                  <TableRow key={row.id} className="cursor-pointer hover:bg-muted/50" tabIndex={0}
                            onClick={() => navigate(`/approvals/${row.id}`)}
                            onKeyDown={e => { if (e.key === "Enter") navigate(`/approvals/${row.id}`); }}
                            data-testid={`approval-row-${row.id}`}>
                    <TableCell className="tabular-nums">{row.id}</TableCell>
                    <TableCell>{typeLabel(row.action_type)}</TableCell>
                    <TableCell className="max-w-[28rem]">
                      <span className="line-clamp-2">{row.summary}</span>
                      <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-micro text-muted-foreground md:hidden">
                        <span>{row.requested_by}</span>
                        <Badge className={`border-transparent ${STATUS_TONE[row.status] || ""}`}>{row.status}</Badge>
                        {row.flags_count > 0 && <Badge className="border-transparent bg-warning-subtle text-warning">{row.flags_count} flag{row.flags_count > 1 ? "s" : ""}</Badge>}
                      </span>
                      {row.last_error && <span className="block text-micro text-danger">Last approval failed: {row.last_error}</span>}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">{row.requested_by}</TableCell>
                    <TableCell className="whitespace-nowrap">{ageText(row.requested_at)}</TableCell>
                    <TableCell className="hidden md:table-cell">{row.flags_count > 0 ? <Badge className="border-transparent bg-warning-subtle text-warning">{row.flags_count}</Badge> : "—"}</TableCell>
                    <TableCell className="hidden md:table-cell"><Badge className={`border-transparent ${STATUS_TONE[row.status] || ""}`}>{row.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
    </PageShell>
  );
}
